import React, { forwardRef, ReactNode, useRef } from 'react';
import clsx from 'clsx';
import { useFocusRing } from '@react-aria/focus';
import { useHover, usePress } from '@react-aria/interactions';
import { useSwitch, AriaSwitchProps } from '@react-aria/switch';
import { filterDOMProps, mergeProps } from '@react-aria/utils';
import { useFocusableRef } from '@react-spectrum/utils';
import { useToggleState } from '@react-stately/toggle';
import {
  FocusableRef,
  FocusableProps,
  FocusableDOMProps,
  AriaLabelingProps,
  InputBase,
} from '@react-types/shared';
import styles from '@danske/sapphire-css/components/switch/switch.module.css';

import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import {
  AriaLabelingPropsKeys,
  AriaTogglePropsEssentialKeys,
  BaseComponentKeys,
  GlobalDomAttributes,
} from '../../utils/types';

export interface SwitchProps
  extends InputBase,
    FocusableProps,
    FocusableDOMProps,
    Pick<
      AriaSwitchProps,
      | BaseComponentKeys
      | 'children'
      | AriaLabelingPropsKeys
      | 'name'
      | 'id'
      | AriaTogglePropsEssentialKeys
      | 'value'
    >,
    Pick<AriaLabelingProps, AriaLabelingPropsKeys>,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * @default
   */
  size?: 'lg' | 'md';
  /**
   * If set, the switch displays note text below the main label.
   */
  note?: ReactNode;
}

export const Switch = forwardRef(function Switch(
  props: SwitchProps,
  ref: FocusableRef<HTMLLabelElement>
) {
  useThemeCheck();

  const {
    isDisabled = false,
    autoFocus,
    children,
    note,
    size = 'lg',
    ...otherProps
  } = props;
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { pressProps, isPressed } = usePress({});
  const inputRef = useRef<HTMLInputElement>(null);
  const domRef = useFocusableRef(ref, inputRef);
  const { isFocusVisible, focusProps } = useFocusRing({ autoFocus });
  const state = useToggleState(props);
  const { inputProps } = useSwitch(props, state, inputRef);
  const { styleProps } = useSapphireStyleProps(props);

  return (
    <div
      className={styles['sapphire-switch-container']}
      {...filterDOMProps(otherProps, { global: true })}
    >
      <label
        {...hoverProps}
        ref={domRef}
        className={clsx(
          styles['sapphire-switch'],
          styles['js-focus'],
          styles['js-hover'],
          {
            [styles['is-hover']]: isHovered,
            [styles['is-focus']]: isFocusVisible,
            [styles['is-active']]: isPressed,
            [styles['sapphire-switch--md']]: size === 'md',
          },
          styleProps.className
        )}
        style={styleProps.style}
      >
        <input
          {...mergeProps(focusProps, inputProps, pressProps)}
          ref={inputRef}
          className={styles['sapphire-switch-input']}
        />
        <span className={styles['sapphire-switch-track']} />
        {children && (
          <span className={styles['sapphire-switch-label']}>{children}</span>
        )}
      </label>
      {typeof note === 'string' && (
        <div
          className={clsx(styles['sapphire-switch__note'], {
            [styles['sapphire-switch__note--md']]: size === 'md',
          })}
        >
          {note}
        </div>
      )}
    </div>
  );
});
